import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Router } from 'react-router-dom';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { User } from 'oidc-client-ts';

import './index.css';
import App from './App';
import { Provider } from './core/stores/RootStore';
import { setupRootStore } from './core/setup-mobx';
import { triggerSignin, userManager } from './core/auth';

const { rootStore, history } = setupRootStore();

const favicon = import.meta.env.VITE_DO_MODE === 'vision' ? '/vision.png' : '/favicon.ico';
document.querySelector('link[rel="icon"]').setAttribute('href', favicon);

const onSignInCallback = (_user: User | void): void => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

const AuthEventsBridge: React.FC = () => {
  const auth = useAuth();

  useEffect(() => {
    const cleanupSilentRenewError = auth.events.addSilentRenewError(triggerSignin);
    const cleanupUserSignedOut = auth.events.addUserSignedOut(triggerSignin);
    const cleanupTokenExpiring = auth.events.addAccessTokenExpiring(() => {
      auth.signinSilent().catch(error => {
        console.error('refresh token error:', error);
      });
    });

    return () => {
      cleanupSilentRenewError();
      cleanupUserSignedOut();
      cleanupTokenExpiring();
    };
  }, [auth]);

  return null;
};

ReactDOM.render(
  <React.Suspense fallback={<></>}>
    <AuthProvider userManager={userManager} onSigninCallback={onSignInCallback}>
      <Provider value={rootStore}>
        <Router history={history}>
          <AuthEventsBridge />
          <App />
        </Router>
      </Provider>
    </AuthProvider>
  </React.Suspense>,
  document.getElementById('root'),
);
